import { Hit } from "../../../../data-model/algoliaSearchModel";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { BOOptionModel } from "../../../../data-model/boOptionModel";
import { EvaluationResult } from "../../../../data-model/nccnGuidelines";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import {
  MedicalPaFilterKey,
  NCCN_AI_EVALUATION_NOT_RUN,
} from "../../../../enums/medicalPAFilterKeys";
import { MedicalOrderReviewStatus } from "../../../../enums/orderReviewStatus";
import { OrderStateStatus } from "../../../../enums/orderStateStatus";
import { FacilityProvider } from "../../../../redux/slice/providerSlice";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";

/**
 * Extracts unique drug/regimen names from a set of medical PA order hits.
 *
 * @param medicalPaOrders - Array of Algolia/Postgres Hit objects
 * @returns Deduplicated array of regimen names
 */
const getUniqueDrugNamesFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allDrugNames = medicalPaOrders.map((order) => order?.regimen_name);
  const uniqueDrugNames = [...new Set(allDrugNames)];
  return uniqueDrugNames;
};

/**
 * Extracts unique primary payer names from a set of medical PA order hits.
 *
 * @param medicalPaOrders - Array of Algolia/Postgres Hit objects
 * @returns Deduplicated array of payer names
 */
const getUniquePlanDetailsFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allPlanDetails = medicalPaOrders.map(
    (order) => order?.primary_payer_name,
  );
  const uniquePlanDetails = [...new Set(allPlanDetails)];
  return uniquePlanDetails;
};

/**
 * Extracts unique practitioner names from a set of medical PA order hits.
 *
 * @param medicalPaOrders - Array of Algolia/Postgres Hit objects
 * @returns Deduplicated array of practitioner names
 */
const getUniquePractitionerNamesFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allPractitionerNames = medicalPaOrders.map(
    (order) => order?.practitioner_name,
  );
  const uniquePractitionerNames = [...new Set(allPractitionerNames)];
  return uniquePractitionerNames;
};

/**
 * Extracts unique locations from a set of medical PA order hits.
 *
 * @param medicalPaOrders - Array of Algolia/Postgres Hit objects
 * @returns Deduplicated array of location strings
 */
const getUniqueLocationsFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allLocations = medicalPaOrders.map((order) => order?.location);
  const uniqueLocations = [...new Set(allLocations)];
  return uniqueLocations;
};

/**
 * Creates a filter section for medication/drug names derived from order hits.
 *
 * @param orders - Array of order hits to extract drug names from
 * @returns Filter configuration with sorted medication options
 */
export const createFilterForDrugNamesForAlgolia = (orders: Hit[]) => {
  const drugNames = getUniqueDrugNamesFromMedicalPaOrdersOfAlgolia(orders);
  const drugNameOptions = drugNames.map((drugName) => ({
    label: drugName ?? "",
    value: drugName ?? "",
  }));
  return {
    id: MedicalPaFilterKey.RegimenNames,
    label: "Medication",
    options: drugNameOptions.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForDateOfServiceForAlgolia = () => {
  return {
    id: MedicalPaFilterKey.DateOfService,
    label: "Date of Service",
    options: [],
    type: "date" as const,
  };
};

export const createFilterForCreatedAtForAlgolia = () => {
  return {
    id: MedicalPaFilterKey.CreatedAt,
    label: "Date of Creation",
    options: [],
    type: "date" as const,
  };
};

export const createFilterForDateOfWorkForAlgolia = () => {
  return {
    id: MedicalPaFilterKey.DateOfWork,
    label: "Date of Work",
    options: [],
    type: "date" as const,
  };
};

/**
 * Creates a filter section for BO (Benefit Order) status options.
 *
 * @param boOptions - Array of BO option models with label/value pairs
 * @returns Filter configuration with sorted BO status options
 */
export const createFilterForBoStatusForAlgolia = (
  boOptions: BOOptionModel[],
) => {
  return {
    id: MedicalPaFilterKey.BoStatus,
    label: "BO Status",
    options: boOptions
      .map((boOption) => ({
        label: boOption.label,
        value: boOption.value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

/**
 * Creates a filter section for Estimate Value options (exception-flow urology orgs).
 *
 * Options are loaded from Firestore `estimate_options`; the section is keyed
 * `estimate_value` so the generated query filters on that field.
 *
 * @param estimateOptions - Estimate option models from Redux `estimateOptions`
 * @returns Filter configuration with sorted estimate value options
 */
export const createFilterForEstimateValueForAlgolia = (
  estimateOptions: BOOptionModel[],
) => {
  return {
    id: MedicalPaFilterKey.EstimateValue,
    label: "Estimate",
    options: estimateOptions
      .map((estimateOption) => ({
        label: estimateOption.label,
        value: estimateOption.value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForEvWriteBackStatusForAlgolia = () => {
  return {
    id: MedicalPaFilterKey.EvWriteBackStatus,
    label: "Write Back Status",
    options: Object.values(EVWriteBackStatus).map((evWriteBackOption) => ({
      label: capitalizeWordsSeperatedByUnderScore(evWriteBackOption),
      value: evWriteBackOption,
    })),
    type: "string" as const,
  };
};

export const createFilterForDocumentUploadStatusForAlgolia = () => {
  return {
    id: MedicalPaFilterKey.DocumentUploadStatus,
    label: "Document Upload Status",
    type: "string" as const,
    options: Object.values(DocWriteBackStatus).map((docWriteBackOption) => ({
      label: capitalizeWordsSeperatedByUnderScore(docWriteBackOption),
      value: docWriteBackOption,
    })),
  };
};

export const createFilterOptionsForPlanDetailsForAlgolia = (orders: Hit[]) => {
  const planDetails = getUniquePlanDetailsFromMedicalPaOrdersOfAlgolia(orders);
  return {
    id: MedicalPaFilterKey.PrimaryPayerName,
    label: "Insurance Plans",
    options: planDetails.map((planDetail) => ({
      label: planDetail ?? "",
      value: planDetail ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsBasedOnPractitionerNameForAlgolia = (
  orders: Hit[],
) => {
  const practitionerNames =
    getUniquePractitionerNamesFromMedicalPaOrdersOfAlgolia(orders);
  return {
    id: MedicalPaFilterKey.PractitionerName,
    label: "Practitioner Name",
    options: practitionerNames.map((practitionerName) => ({
      label: practitionerName ?? "",
      value: practitionerName ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsBasedOnLocationForAlgolia = (orders: Hit[]) => {
  const locations = getUniqueLocationsFromMedicalPaOrdersOfAlgolia(orders);
  return {
    id: MedicalPaFilterKey.Location,
    label: "Location",
    options: locations.map((location) => ({
      label: location ?? "",
      value: location ?? "",
    })),
    type: "string" as const,
  };
};

/**
 * Creates a filter section for auth status using the configured auth status options.
 *
 * @param options - Array of auth status option models
 * @returns Filter configuration with auth status options mapped to label/value
 */
export const createFilterOptionsBasedOnAuthStatusForAlgolia = (
  options: AuthStatusOptionModel[],
) => {
  return {
    id: MedicalPaFilterKey.AuthStatus,
    label: "Auth Status",
    options: options.map((option) => ({
      label: option.text,
      value: option.id,
    })),
    type: "string" as const,
  };
};

/**
 * Creates a filter section for order assignees from facility providers.
 * Includes an "Unassigned" option that is sorted alphabetically alongside the
 * provider names by the central sorting in `buildFilterData`. Medical PA
 * specific — not used in external orders.
 *
 * @param allProviders - Array of facility provider records
 * @returns Filter configuration with provider names plus an unassigned option
 */
export const createFiltersForAssigneeForAlgolia = (
  allProviders: FacilityProvider[],
) => {
  const providerOptions = allProviders.map((provider) => ({
    label: generateFullName(
      provider?.FirstName ?? provider?.name ?? "",
      "",
      provider?.LastName ?? "",
    ),
    value: provider.id,
  }));

  const unAssignedOption = {
    label: "Unassigned",
    value: "unassigned",
  };

  return {
    id: MedicalPaFilterKey.Assignee,
    label: "Assignee",
    options: [...providerOptions, unAssignedOption],
    type: "string" as const,
  };
};

/**
 * Creates a filter section for regimen names from pre-aggregated multi-facet count results.
 *
 * @param options - Array of regimen name strings from facet counts
 * @returns Filter configuration for regimen name selection
 */
export const createFilterForRegimenNamesFromAlgoliaMultiFacetsCount = (
  options: string[],
) => {
  return {
    id: MedicalPaFilterKey.RegimenNames,
    label: "Regimen Names",
    options: options.map((regimenName) => ({
      label: regimenName ?? "",
      value: regimenName ?? "",
    })),
    type: "string" as const,
  };
};

/**
 * Creates a filter section for insurance plan details from pre-aggregated multi-facet count results.
 *
 * @param options - Array of insurance plan name strings from facet counts
 * @returns Filter configuration for insurance plan selection
 */
export const createFilterOptionsForPlanDetailsFromAlgoliaMultiFacetsCount = (
  options: string[],
) => {
  return {
    id: MedicalPaFilterKey.PrimaryPayerName,
    label: "Primary Insurance Plans",
    options: options.map((planDetail) => ({
      label: planDetail ?? "",
      value: planDetail ?? "",
    })),
    type: "string" as const,
  };
};

/**
 * Creates a filter section for practitioner names from pre-aggregated multi-facet count results.
 *
 * @param options - Array of practitioner name strings from facet counts
 * @returns Filter configuration for practitioner name selection
 */
export const createFilterOptionsBasedOnPractitionerNameFromAlgoliaMultiFacetsCount =
  (options: string[]) => {
    return {
      id: MedicalPaFilterKey.PractitionerName,
      label: "Practitioner Name",
      options: options.map((practitionerName) => ({
        label: practitionerName ?? "",
        value: practitionerName ?? "",
      })),
      type: "string" as const,
    };
  };

/**
 * Creates a filter section for locations from pre-aggregated multi-facet count results.
 *
 * @param options - Array of location strings from facet counts
 * @returns Filter configuration for location selection
 */
export const createFilterOptionsBasedOnLocationFromAlgoliaMultiFacetsCount = (
  options: string[],
) => {
  return {
    id: MedicalPaFilterKey.Location,
    label: "Location",
    options: options.map((location) => ({
      label: location ?? "",
      value: location ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsForRegimenType = () => {
  return {
    id: MedicalPaFilterKey.RegimenType,
    label: "Regimen Type",
    options: [
      {
        label: "Single Agent",
        value: "single_agent",
      },
      {
        label: "Multi Agent",
        value: "multi_agent",
      },
    ],
    type: "string" as const,
  };
};

/**
 * Creates a filter section for EV (Eligibility Verification) primary status.
 *
 * @param options - Array of EV primary status strings
 * @returns Filter configuration for EV primary status selection
 */
export const createFilterOptionsForEvPrimary = (options: string[]) => {
  return {
    id: MedicalPaFilterKey.EvPrimary,
    label: "EV Primary Status",
    options: options.map((option) => ({
      label: capitalizeWordsSeperatedByUnderScore(option ?? ""),
      value: option ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsForOrderStateStatus = () => {
  return {
    id: MedicalPaFilterKey.OrderStateStatus,
    label: "Order Status",
    options: Object.values(OrderStateStatus).map((orderStateStatus) => ({
      label: capitalizeWordsSeperatedByUnderScore(orderStateStatus),
      value: orderStateStatus,
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsForReviewStatus = () => {
  return {
    id: MedicalPaFilterKey.ReviewStatus,
    label: "Review Status",
    options: Object.values(MedicalOrderReviewStatus).map((reviewStatus) => ({
      label: capitalizeWordsSeperatedByUnderScore(reviewStatus),
      value: reviewStatus,
    })),
    type: "string" as const,
  };
};

/**
 * Creates the "AI Verdict (Medical Necessity)" filter section.
 *
 * Filters on the NCCN order-level `ai_evaluation` column — the AI's own verdict,
 * independent of any later human review or recompute. "Not Run" is a sentinel
 * (`NCCN_AI_EVALUATION_NOT_RUN`) that the backend maps to orders with no
 * `nccn_guideline_evaluations` row.
 *
 * @returns Filter configuration backed by MedicalPaFilterKey.NccnAiEvaluation
 */
export const createFilterOptionsForNccnAiEvaluation = () => {
  const options: {
    label: string;
    value: EvaluationResult | typeof NCCN_AI_EVALUATION_NOT_RUN;
  }[] = [
    { label: "Met", value: "True" },
    { label: "Not Met", value: "False" },
    { label: "Undetermined", value: "Undetermined" },
    { label: "Not Run", value: NCCN_AI_EVALUATION_NOT_RUN },
  ];
  return {
    id: MedicalPaFilterKey.NccnAiEvaluation,
    label: "AI Verdict (Medical Necessity)",
    options,
    type: "string" as const,
  };
};

/**
 * Creates the "Human Validation (Medical Necessity)" filter section.
 *
 * Filters on the derived `nccn_human_validation` facet (backend CASE over
 * review_submitted, ai_evaluation, and ai_review_refuted).
 * Orders where Medical Necessity did not run are excluded by every option.
 *
 * @returns Filter configuration backed by MedicalPaFilterKey.NccnHumanValidation
 */
export const createFilterOptionsForNccnHumanValidation = () => {
  return {
    id: MedicalPaFilterKey.NccnHumanValidation,
    label: "Human Validation (Medical Necessity)",
    options: [
      { label: "Agreed", value: "agreed" },
      { label: "Disagreed", value: "disagreed" },
      { label: "AI Inconclusive", value: "ai_inconclusive" },
      { label: "Yet To Validate", value: "yet_to_validate" },
    ],
    type: "string" as const,
  };
};

export const createFilterOptionsForSecondaryPayerName = (options: string[]) => {
  return {
    id: MedicalPaFilterKey.SecondaryPayerName,
    label: "Secondary Insurance Plans",
    options: options
      .map((option) => ({
        label: capitalizeWordsSeperatedByUnderScore(option ?? ""),
        value: option ?? "",
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};
